// Measures computed at query time (SPEC §5) and the freshness levels (SPEC §11.2).

/** Default bucket edges (admin settings, SPEC §13.1). */
export const DEFAULT_AGING_EDGES: [number, number, number] = [7, 14, 30];
export const AGING_BUCKET_LABELS = (e: readonly number[]) => [
  `0–${e[0]} d`,
  `${e[0]! + 1}–${e[1]} d`,
  `${e[1]! + 1}–${e[2]} d`,
  `> ${e[2]} d`,
];
/** Fixed cycle-time buckets, upper bounds in days (SPEC §5.1). */
export const CYCLE_BUCKET_EDGES = [15, 30, 45, 60] as const;
export const CYCLE_BUCKET_LABELS = ["≤ 15 d", "16–30 d", "31–45 d", "46–60 d", "> 60 d"];
export const DEFAULT_CYCLE_TARGET_DAYS = 45;

export function agingBucket(days: number, edges: readonly number[]): number {
  const i = edges.findIndex((e) => days <= e);
  return i === -1 ? edges.length : i;
}

export function cycleBucket(days: number): number {
  return agingBucket(days, CYCLE_BUCKET_EDGES);
}

export function median(xs: number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

export type FreshnessLevel = "fresh" | "stale" | "severe";
export type FreshnessThresholds = { staleMinutes: number; severeMinutes: number };
export const DEFAULT_FRESHNESS: FreshnessThresholds = { staleMinutes: 15, severeMinutes: 60 };

/** Level for an "as of" that is `ageMinutes` old; null means no successful delta yet. */
export function freshnessLevel(ageMinutes: number | null, t: FreshnessThresholds): FreshnessLevel {
  if (ageMinutes === null || ageMinutes > t.severeMinutes) return "severe";
  return ageMinutes > t.staleMinutes ? "stale" : "fresh";
}

export const REQ_TYPE_VALUES = ["vessel", "local", "land", "service"] as const;
export type ReqType = (typeof REQ_TYPE_VALUES)[number];
/** Sertica's names for its requisition types; the keys stay as filter values in URLs. */
export const REQ_TYPE_LABEL: Record<ReqType, string> = { vessel: "Normal unit", local: "Local unit", land: "Office", service: "Service order" };

/** Requisition type filter value for Sertica's `requisitionType` (SPEC §2.1). */
export function reqTypeOf(v: string | null | undefined): ReqType {
  if (v === "LocalShip") return "local";
  if (v === "NormalLand") return "land";
  if (v === "ServiceOrder") return "service";
  return "vessel";
}
